/**
 * Linked Excel Editor Agent - Tools Section
 * 
 * Standard tools section with preamble and postamble from Keywords AI.
 */

import { PromptSectionBase } from '../../base/systemPrompt/PromptSectionBase';
import type { ToolBase } from '../../base/toolBase';
import { LINKED_EXCEL_PROMPT_KEYS } from './variables';

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS PREAMBLE
// ═══════════════════════════════════════════════════════════════════════════

class LinkedExcelEditorToolsPreambleSection extends PromptSectionBase {
    protected readonly keywordsKey = LINKED_EXCEL_PROMPT_KEYS.toolsPreamble;
    protected readonly fallbackContent = `## Available Tools

The following direct tools are available in this monolithic runtime:
- \`readExcel\`
- \`editContent\`
- \`editFormat\`
- \`editChart\`
- \`editStructure\`
- submit toolkit tool(s) (\`submitResults\` and optional \`submitArtifact\` in legacy mode)
- optional \`userInterview\` when enabled`;
}

const toolsPreambleSection = new LinkedExcelEditorToolsPreambleSection();

export function getToolsPreamble(): string {
    return toolsPreambleSection.getContent();
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS POSTAMBLE
// ═══════════════════════════════════════════════════════════════════════════

class LinkedExcelEditorToolsPostambleSection extends PromptSectionBase {
    protected readonly keywordsKey = LINKED_EXCEL_PROMPT_KEYS.toolsPostamble;
    protected readonly fallbackContent = `## Tool Usage Notes

**Workflow:**
1. Read first - Always understand current state before editing
2. Use direct tools in focused calls (content/format/chart/structure)
3. For chart edits, enforce readExcel.readCharts before/after and preserve readId lineage when available
4. Submit based on toolkit mode:
   - Legacy mode: submitArtifact (optional) then submitResults
   - Override mode: single submitResults with optional artifactsToCreate`;
}

const toolsPostambleSection = new LinkedExcelEditorToolsPostambleSection();

export function getToolsPostamble(): string {
    return toolsPostambleSection.getContent();
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS SECTION BUILDER
// ═══════════════════════════════════════════════════════════════════════════

export function buildToolsSection(tools: ToolBase[]): string {
    const parts: string[] = [];
    
    // 1. Preamble
    parts.push(getToolsPreamble());
    parts.push('');
    
    // 2. Individual tool descriptions
    for (const tool of tools) {
        parts.push(tool.systemPromptDescription);
        parts.push('');
    }
    
    // 3. Postamble
    parts.push(getToolsPostamble());
    
    return parts.join('\n');
}
